import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../../components/data-table";
import { Button } from "../../../components/ui/button";
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  Tag,
  Eye,
  Info,
  ImagePlus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { toast } from "sonner";
import { eventsAPI, type JBFEvent } from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

const initialFormState = {
  event_for: "",
  address: "",
  event_title: "",
  start_date: "",
  end_date: "",
  number_of_days: "1",
  description: "",
  image: null as File | null,
};

export default function Events() {
  const [events, setEvents] = useState<JBFEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [form, setForm] = useState(initialFormState);
  const [viewingEvent, setViewingEvent] = useState<JBFEvent | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data: any = await eventsAPI.list({
        page: currentPage,
        page_size: pageSize,
      });

      if (data && "data" in data) {
        setEvents(Array.isArray(data.data) ? data.data : []);
        setTotalItems(data.count ?? 0);
        setTotalPages(Math.ceil((data.count ?? 0) / pageSize));
      } else if (Array.isArray(data)) {
        setEvents(data);
        setTotalItems(data.length);
        setTotalPages(1);
      } else {
        setEvents([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error loading events:", error);
      toast.error("Failed to load events");
      setEvents([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (form.start_date && form.end_date) {
      const start = new Date(form.start_date);
      const end = new Date(form.end_date);
      if (start <= end) {
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setForm(prev => ({ ...prev, number_of_days: String(diffDays) }));
      } else {
        setForm(prev => ({ ...prev, number_of_days: "0" }));
      }
    }
  }, [form.start_date, form.end_date]);

  const resetForm = () => {
    setForm(initialFormState);
    setImagePreview(null);
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditModal = (event: JBFEvent) => {
    setForm({
      event_for: event.event_for ?? "",
      address: event.address ?? "",
      event_title: event.event_title ?? "",
      start_date: event.start_date ?? "",
      end_date: event.end_date ?? "",
      number_of_days: String(event.number_of_days ?? "1"),
      description: event.description ?? "",
      image: null,
    });
    if (event.image) {
      setImagePreview(event.image);
    }
    setEditingId(event.id);
    setIsDialogOpen(true);
  };

  const openViewModal = (event: JBFEvent) => {
    setViewingEvent(event);
  };

  const handleSave = async () => {
    if (!form.event_title.trim()) {
      toast.error("Please enter an event title.");
      return;
    }
    if (!form.start_date) {
      toast.error("Please select a start date.");
      return;
    }
    if (!form.end_date) {
      toast.error("Please select an end date.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        number_of_days: parseInt(form.number_of_days) || 1,
      };

      if (editingId !== null) {
        await eventsAPI.partialUpdate(editingId, payload);
        toast.success("Event updated successfully!");
      } else {
        await eventsAPI.create(payload);
        toast.success("Event created successfully!");
      }
      await loadEvents();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(
        editingId !== null ? "Failed to update event" : "Failed to create event"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId === null) {
      return;
    }
    try {
      await eventsAPI.delete(deleteId);
      toast.success("Event deleted successfully!");
      await loadEvents();
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  const isEditMode = editingId !== null;

  const tableColumns = useMemo<TableColumnDef<JBFEvent>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 80,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "eventInfo",
        label: "Event Info",
        width: 300,
        renderCell: event => (
          <div className="flex flex-col">
            <span className="font-semibold text-foreground">
              {event.event_title}
            </span>
            <span className="line-clamp-1 text-xs text-muted-foreground">
              {event.description}
            </span>
          </div>
        ),
      },
      {
        id: "category",
        label: "Category",
        width: 140,
        renderCell: event => (
          <div className="flex items-center gap-1.5 capitalize">
            <div className="rounded-md bg-primary/10 p-1">
              <Tag className="h-3 w-3 text-primary" />
            </div>
            <span className="text-sm">{event.event_for}</span>
          </div>
        ),
      },
      {
        id: "location",
        label: "Location",
        width: 160,
        renderCell: event => (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="max-w-[150px] truncate text-sm">
              {event.address}
            </span>
          </div>
        ),
      },
      {
        id: "startDate",
        label: "Start Date",
        width: 130,
        renderCell: event => (
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            {event.start_date}
          </div>
        ),
      },
      {
        id: "endDate",
        label: "End Date",
        width: 130,
        renderCell: event => (
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            {event.end_date}
          </div>
        ),
      },
      {
        id: "duration",
        label: "Duration",
        width: 100,
        renderCell: event => (
          <span className="text-xs text-muted-foreground">
            {event.number_of_days} Day&apos;s
          </span>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 140,
        alwaysVisible: true,
        headerClassName: "text-center",
        cellClassName: "text-center",
        renderCell: event => (
          <div className="flex items-center justify-center gap-2">
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9 rounded-xl border-input bg-blue-50/50 transition-all hover:border-white hover:bg-accent"
              onClick={() => openViewModal(event)}
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9 rounded-xl border-input bg-blue-50/50 transition-all hover:border-white hover:bg-accent"
              onClick={() => openEditModal(event)}
              title="Edit Event"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-9 w-9 rounded-xl border-red-100 bg-red-50/50 transition-all hover:border-red-200 hover:bg-red-100/80"
              onClick={() => setDeleteId(event.id)}
              title="Delete Event"
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Events Management
          </h1>
          <p className="text-muted-foreground">
            Create and manage activities and events across categories
          </p>
        </div>
        <Button onClick={openAddModal} className="w-full gap-2 md:w-auto">
          <Plus className="h-4 w-4" />
          Add Event
        </Button>
      </div>

      <Card className="border-none shadow-md">
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <CustomLoader size="md" />
            </div>
          ) : (
            <DataTable
              title={`All Events (${totalItems})`}
              tableId="super-admin-cms-events"
              columns={tableColumns}
              data={events}
              getRowKey={event => event.id}
              emptyMessage="No events found. Click Add Event to get started."
              rowClassName={() => "hover:bg-muted/20"}
              headerRowClassName="bg-muted/50 hover:bg-muted/50"
            />
          )}

          {!loading && totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl sm:p-8">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isEditMode ? "Edit Event" : "Add New Event"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="event_for">Event For</Label>
                <Select
                  value={form.event_for}
                  onValueChange={val =>
                    setForm(prev => ({ ...prev, event_for: val }))
                  }
                >
                  <SelectTrigger
                    id="event_for"
                    className="h-11 border-none bg-muted/50 shadow-none"
                  >
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Workshop">Workshop</SelectItem>
                    <SelectItem value="Hostel">Hostel</SelectItem>
                    <SelectItem value="Institution">Institution</SelectItem>
                    <SelectItem value="Boarding">Boarding</SelectItem>
                    <SelectItem value="Seminar">Seminar</SelectItem>
                    <SelectItem value="Meeting">Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="event_title">Event Title</Label>
                <Input
                  id="event_title"
                  placeholder="e.g. Annual Meet 2024"
                  className="h-11 border-none bg-muted/50 shadow-none"
                  value={form.event_title}
                  onChange={e =>
                    setForm(prev => ({ ...prev, event_title: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address / Location</Label>
              <Input
                id="address"
                placeholder="Enter full address"
                className="h-11 border-none bg-muted/50 shadow-none"
                value={form.address}
                onChange={e =>
                  setForm(prev => ({ ...prev, address: e.target.value }))
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  className="h-11 border-none bg-muted/50 shadow-none"
                  value={form.start_date}
                  onChange={e =>
                    setForm(prev => ({ ...prev, start_date: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  className="h-11 border-none bg-muted/50 shadow-none"
                  value={form.end_date}
                  onChange={e =>
                    setForm(prev => ({ ...prev, end_date: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="number_of_days">Number of Days</Label>
                <Input
                  id="number_of_days"
                  type="number"
                  min="1"
                  readOnly
                  className="h-11 cursor-not-allowed border-none bg-muted/30 shadow-none focus-visible:ring-0"
                  value={form.number_of_days}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image" className="text-sm font-medium">
                  Event Image
                </Label>
                <div className="flex items-center gap-3">
                  {imagePreview && (
                    <div
                      className="relative h-11 w-11 shrink-0 cursor-zoom-in overflow-hidden rounded-lg border border-primary/20 bg-muted shadow-sm transition-transform hover:scale-105"
                      onClick={() => setIsPreviewOpen(true)}
                    >
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <div className="relative h-11 flex-1">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setForm(prev => ({ ...prev, image: file }));
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setImagePreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <div className="flex h-11 w-full items-center justify-between rounded-lg bg-muted/50 px-4 text-sm text-foreground/80 transition-colors hover:bg-muted/70">
                      <span className="max-w-[180px] truncate">
                        {form.image ? form.image.name : "Choose image..."}
                      </span>
                      <ImagePlus className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Event Description</Label>
              <Textarea
                id="description"
                placeholder="Briefly describe the event..."
                className="min-h-[100px] resize-none border-none bg-muted/50 shadow-none"
                value={form.description}
                onChange={e =>
                  setForm(prev => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="h-11 px-8"
            >
              {saving && <CustomLoader size="xs" className="mr-2" />}
              {isEditMode ? "Update Changes" : "Save Event"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Event Dialog */}
      <Dialog
        open={viewingEvent !== null}
        onOpenChange={open => !open && setViewingEvent(null)}
      >
        <DialogContent className="max-w-xl overflow-hidden border-none shadow-2xl sm:p-0">
          <div className="border-b border-primary/10 bg-primary/5 p-6">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-2.5">
                  <Info className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-foreground">
                    Event Details
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    Comprehensive information about the activity
                  </p>
                </div>
              </div>
            </DialogHeader>
          </div>

          {viewingEvent && (
            <div className="space-y-8 p-6 md:p-8">
              <div className="grid gap-6">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80">
                    Title
                  </span>
                  <p className="text-2xl font-bold leading-tight text-foreground">
                    {viewingEvent.event_title}
                  </p>
                </div>

                {viewingEvent.image && (
                  <div className="overflow-hidden rounded-2xl border border-primary/10">
                    <img
                      src={viewingEvent.image}
                      alt={viewingEvent.event_title}
                      className="aspect-video w-full object-cover"
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2.5 rounded-2xl bg-muted/40 px-4 py-2.5">
                    <Tag className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold capitalize">
                      {viewingEvent.event_for}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-2xl bg-muted/40 px-4 py-2.5">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-semibold">
                      {viewingEvent.start_date} - {viewingEvent.end_date}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-2xl bg-muted/40 px-4 py-2.5">
                    <CustomLoader size="xs" />
                    <span className="text-sm font-semibold">
                      {viewingEvent.number_of_days} Day&apos;s
                    </span>
                  </div>
                </div>

                <div className="space-y-4 pt-4">
                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/60">
                      <MapPin className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80">
                        Location Address
                      </span>
                      <p className="font-medium text-foreground">
                        {viewingEvent.address}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-dashed pt-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80">
                      Description
                    </span>
                    <div className="mt-3 rounded-2xl border border-muted-foreground/5 bg-muted/30 p-5">
                      <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">
                        {viewingEvent.description ||
                          "No additional details provided for this event."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  variant="secondary"
                  className="h-11 rounded-xl bg-muted/80 px-10 font-bold hover:bg-muted"
                  onClick={() => setViewingEvent(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteId !== null}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold">
              Are you absolutely sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              This action cannot be undone. This will permanently delete the
              event
              <span className="font-bold text-foreground">
                {" "}
                {events.find(e => e.id === deleteId)?.event_title}
              </span>{" "}
              from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-3">
            <AlertDialogCancel className="h-11 rounded-xl border-none bg-muted/50 font-semibold">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="text-destructive-foreground h-11 rounded-xl bg-destructive px-6 font-semibold hover:bg-destructive/90"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ImagePreviewModal
        isOpen={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        src={imagePreview}
        title="Event Image Preview"
      />
    </div>
  );
}

function ImagePreviewModal({
  isOpen,
  onOpenChange,
  src,
  title,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  src: string | null;
  title: string;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-hidden p-0 shadow-2xl">
        <div className="p-1">
          {src ? (
            <img
              src={src}
              alt={title}
              className="h-auto max-h-[80vh] w-full rounded-lg object-contain"
            />
          ) : (
            <div className="flex h-64 items-center justify-center bg-muted">
              <span className="text-muted-foreground">No image available</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
